import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { surveysApi } from '../features/surveys/api.ts';
import { SurveyAnswerForm } from '../components/SurveyAnswerForm.tsx';

interface ApiError { response?: { data?: { error?: string } } }

export function SurveyResponseEdit(): JSX.Element {
  const { surveyId, rid } = useParams({ from: '/surveys/$surveyId/responses/$rid/edit' });
  const qc = useQueryClient();
  const navigate = useNavigate();

  const surveyQ = useQuery({
    queryKey: ['survey', surveyId],
    queryFn: () => surveysApi.get(surveyId),
  });
  // No single-response endpoint — load the (island-scale) list and pick by id.
  const responsesQ = useQuery({
    queryKey: ['survey-responses', surveyId],
    queryFn: () => surveysApi.listResponses(surveyId),
  });

  const save = useMutation({
    mutationFn: (answers: Record<string, unknown>) => surveysApi.updateResponse(surveyId, rid, answers),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['survey-results', surveyId] });
      void qc.invalidateQueries({ queryKey: ['survey-responses', surveyId] });
      void navigate({ to: '/surveys/$surveyId/results', params: { surveyId }, search: { tab: 'responses' } });
    },
  });

  const isLoading = surveyQ.isLoading || responsesQ.isLoading;
  const survey = surveyQ.data;
  const response = responsesQ.data?.find((r) => r.id === rid);

  if (isLoading) {
    return (
      <div className="page-narrow">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (surveyQ.isError || responsesQ.isError || !survey || !response) {
    return (
      <div className="page-narrow">
        <div className="card-paper flex flex-col items-center gap-3 p-10 text-center">
          <Icon name="assignment" size={32} className="text-muted-foreground" />
          <h1 className="font-serif text-xl">Response not found</h1>
          <p className="max-w-sm text-sm text-muted-foreground">
            It may have been deleted, or this survey no longer exists.
          </p>
          <Link to="/surveys/$surveyId/results" params={{ surveyId }} search={{ tab: 'responses' }} className="btn-ghost mt-1">
            ← Back to results
          </Link>
        </div>
      </div>
    );
  }

  const apiErrCode = (save.error as ApiError | null)?.response?.data?.error;

  return (
    <div className="page-narrow space-y-5">
      <header className="space-y-3">
        <Link to="/surveys/$surveyId/results" params={{ surveyId }} search={{ tab: 'responses' }} className="btn-ghost -ms-2">
          ← Back to results
        </Link>
        <p className="status-label">Edit response</p>
        <h1 className="font-serif text-2xl font-bold leading-tight">{survey.title}</h1>
        <p className="mono-meta text-muted-foreground">{response.submittedByName}</p>
      </header>

      {save.isError && (
        <p className="text-sm text-destructive" role="alert">
          {apiErrCode === 'missing_required'
            ? 'Please answer all required questions.'
            : apiErrCode === 'invalid_answer'
              ? 'One of the answers isn’t valid for its question.'
              : apiErrCode === 'survey_not_editable'
                ? 'This survey’s responses can’t be edited.'
                : 'Couldn’t save the changes. Check your connection and try again.'}
        </p>
      )}

      <SurveyAnswerForm
        questions={survey.questions}
        initialAnswers={response.answers}
        submitLabel="Save changes"
        onSubmit={(answers) => save.mutate(answers)}
        pending={save.isPending}
      />
    </div>
  );
}
